<template>
  <div class="recent-view">
    <div class="container-fluid">
      <section>
        <!-- Checking subscription state -->
        <div v-if="isCheckingAccess" class="game-container">
          <div class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t("Checking subscription...") }}</p>
          </div>
        </div>

        <!-- Game iframe -->
        <div v-else-if="canPlay" class="game-container">
          <iframe
            v-if="showGame"
            ref="gameIframe"
            :src="gameUrl"
            class="w-100 game-frame"
            @load="handleGameLoad"
            @error="handleGameError"
          ></iframe>
          
          <div v-if="isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t("Loading game...") }}</p>
          </div>
        </div>

        <!-- Access denied -->
        <div v-else class="access-denied-container">
          <div class="access-denied-content">
            <h1 class="text-white text-center p-3 subscription-message" @click="openSubscription">
              <span v-if="isLoggedIn">
                {{ $t("Your subscription finished, top-up balance and") }}
                {{ $t("activate subscription") }}.
              </span>
              <span v-else>
                {{ $t("Please login to play this game") }}, {{ $t("click") }}
                {{ $t("here") }} {{ $t("to login") }}.
              </span>
            </h1>
          </div>
        </div>

        <!-- Rest of your UI remains the same -->
        <div class="d-flex flex-column flex-md-row justify-content-between gap-4 my-4">
          <div class="d-flex flex-column mx-2">
            <div class="d-flex justify-content-between">
              <div>
                <h4>{{ game.title }}</h4>
                <p class="small">
                  {{ $t("Played") }} {{ game.views ? game.views : 0 }}
                  {{ $t("times") }}.
                </p>
              </div>
              <div class="d-flex align-items-center justify-content-center rounded-circle bg-dark py-2 px-4 d-md-none"
                @click="toggleFullscreen">
                <img src="@/assets/img/game/fullscreen.svg" alt="fullscreen" />
              </div>
            </div>
            <div class="d-flex align-items-center">
              <StarRating :rating="game.ratings_avg" starSize="18" :readonly="true" />
              <span class="small">
                {{ game.ratings_avg }} ({{ game.ratings_count }}
                {{ $t("Reviews") }})
              </span>
            </div>
            <div class="d-flex flex-wrap">
              <img src="@/assets/img/share/icons8-facebook-500.png" alt="facebook" class="cursor-pointer me-2"
                style="width: 40px; height: 40px" @click="share('facebook', game)" />
              <img src="@/assets/img/share/icons8-twitterx-500.png" alt="twitter" class="cursor-pointer me-2"
                style="width: 40px; height: 40px" @click="share('x', game)" />
              <img src="@/assets/img/share/icons8-instagram.svg" alt="instagram" class="cursor-pointer me-2"
                style="width: 48px; height: 40px" @click="share('instagram', game)" />
              <img src="@/assets/img/share/icons8-linkedin-500.png" alt="linkedin" class="cursor-pointer me-2"
                style="width: 40px; height: 40px" @click="share('linkedin', game)" />
              <img src="@/assets/img/share/icons8-whatsapp-500.png" alt="whatsapp" class="cursor-pointer"
                style="width: 40px; height: 40px" @click="share('whatsapp', game)" />
            </div>
          </div>
          <div class="p-4 text-center" v-if="isLoggedIn">
            <StarRating @input="changeRating" starSize="42" :rating="userRate" />
            <p class="">{{ $t("Rate this game") }}</p>
          </div>

          <div class="d-none d-md-block mx-2">
            <div class="d-flex align-items-center rounded-circle bg-dark py-2 px-4 cursor-pointer"
              @click="toggleFullscreen">
              <img src="@/assets/img/game/fullscreen.svg" alt="fullscreen" />
              <span class="text-white ms-2">{{ $t("Fullscreen") }}</span>
            </div>
          </div>
        </div>
      </section>

      <hr class="bg-secondary my-3" />

      <section>
        <div class="mx-2 my-3">
          <h4 class="my-2">{{ $t("Description") }}:</h4>
          <p class="text-md text-justify text-gray" v-html="game.description"></p>
        </div>
        <div class="mx-2 my-3">
          <h4 class="my-2">{{ $t("Instructions") }}:</h4>
          <p class="text-md text-justify text-gray" v-html="game.instructions"></p>
        </div>
      </section>

      <hr class="bg-secondary my-3" />
    </div>
    <div class="crime-section pb-60" v-if="similarGames.length > 0">
      <div class="container-fluid">
        <div class="section-wrapper">
          <h4 class="left-title">{{ $t("Similar Games") }}</h4>
        </div>
        <MostViews :items="similarGames" />
      </div>
    </div>
    <div class="crime-section pb-60">
      <div class="container-fluid">
        <div class="section-wrapper">
          <h4 class="left-title">{{ $t("New Games") }}</h4>
        </div>
        <NewGames :items="newGames" />
      </div>
    </div>
    <hr class="bg-secondary my-3" />
  </div>
</template>

<script>
import { useGamesStore } from "@/store/games";
import { mapState, mapActions } from "pinia";
import { useAuthStore } from "@/store/auth";
import helpers from "@/helpers";
import StarRating from "@/components/partials/StarRating.vue";
import MostViews from "@/components/partials/GameRowSlider.vue";
import NewGames from "@/components/partials/GameRoundedSlider.vue";
import { GameManager } from '@/game-manager';

export default {
  name: "GamePage",
  components: {
    StarRating,
    MostViews,
    NewGames,
  },
  data() {
    return {
      canPlay: false,
      showGame: false,
      isLoading: false,
      isCheckingAccess: true,
      gameUrl: null,
      gameManager: null,
      loadTimeout: null,
    };
  },
  computed: {
    ...mapState(useAuthStore, ["isLoggedIn", "userInfo"]),
    ...mapState(useGamesStore, [
      "game",
      "userRate",
      "newGames",
      "similarGames",
      "voteAction",
      "comments",
    ]),
  },
  methods: {
    ...mapActions(useGamesStore, [
      "fetchGame",
      "voteGame",
      "postComment",
      "rateGame",
    ]),
    ...mapActions(useAuthStore, [
      "openPaymentPage",
    ]),
    
    async changeRating(value) {
      await this.rateGame(this.game.id, value);
      helpers.showToast(
        this.$t("Your rating has been saved successfully"),
        this.$t("Success"),
        "success"
      );
    },
    
    openSubscription() {
      localStorage.setItem("redirectUrl", this.$route.fullPath);
      let config = helpers.getConfig();
      if(config.subscription_type == "card"){
        if(!this.isLoggedIn){
          this.$router.push('/login');
        } else {
          this.openPaymentPage()
        }
      } else {
        window.location.href = config.landing_page_url;
      }
    },
    
    toggleFullscreen() {
      if (this.gameManager) {
        this.gameManager.toggleFullscreen();
      }
    },
    
    onEscKey(event) {
      if (event.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen();
      }
    },
    
    handleGameLoad() {
      console.log('Game iframe loaded');
      this.isLoading = false;
      
      // Clear load timeout
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }
      
      // Initialize game manager
      const iframe = this.$refs.gameIframe;
      if (iframe) {
        this.gameManager = new GameManager(iframe);
      }
    },
    
    handleGameError() {
      console.error('Game failed to load');
      this.isLoading = false;
      this.showGame = false;
      
      // Clear load timeout
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }
      
      helpers.showToast(
        this.$t("Failed to load the game. Please try again."),
        this.$t("Error"),
        "error"
      );
    },
    
    checkAccess() {
      // Don't reset states if game is already loaded and playing
      if (this.canPlay && this.gameUrl && !this.isLoading) {
        return;
      }
      
      this.canPlay = false;
      this.showGame = false;
      
      if (!this.game?.url) {
        this.isCheckingAccess = true;
        return;
      }
      
      // Check if game doesn't require subscription (free game)
      if (this.game.is_subscription == 0) {
        this.canPlay = true;
        this.showGame = true;
        this.isCheckingAccess = false;
        return; // Free game, no need to check subscription status
      }
      
      // For subscription games, wait until userInfo is loaded
      // userInfo will be {} initially, then populated from localStorage/API
      if (this.isLoggedIn && Object.keys(this.userInfo).length === 0) {
        // Still loading user info, keep checking state
        this.isCheckingAccess = true;
        return;
      }
      
      // Done checking - now we can determine access
      this.isCheckingAccess = false;
      
      // Game requires subscription - check if user is subscribed
      if (this.isLoggedIn && this.game.url && this.userInfo?.is_subscribed) {
        this.canPlay = true;
        this.showGame = true;
      }
      
      if (this.canPlay) {
        this.gameUrl = this.game.url;
        this.isLoading = true;
        
        // Set a timeout to handle games that hang during loading (30 seconds)
        this.loadTimeout = setTimeout(() => {
          if (this.isLoading) {
            console.warn('Game load timeout - forcing loading state off');
            this.isLoading = false;
          }
        }, 30000);
        
        // Use nextTick to ensure DOM is ready
        this.$nextTick(() => {
          setTimeout(() => {
            this.showGame = true;
          }, 100);
        });
      }
    },
    
    // Clean up game state
    cleanupGame() {
      // Clear load timeout
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }
      
      // Destroy game manager
      if (this.gameManager) {
        this.gameManager.destroy();
        this.gameManager = null;
      }
    },

    share(platform, game) {
      if (!game || !game.id) {
        console.error("Game data is undefined or missing required properties.");
        return;
      }

      const url = `${window.location.origin}/game/${this.$route.params.slug}`;
      const title = encodeURIComponent(game.title);

      switch (platform) {
        case "facebook":
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "x":
          window.open(
            `https://twitter.com/intent/tweet?text=${title}&url=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "instagram":
          helpers.showToast(
            this.$t("To share on Instagram, copy the game link and share it on your story or post."),
            this.$t("Success"),
            "success"
          );
          navigator.clipboard.writeText(url);
          break;
        case "whatsapp":
          window.open(
            `https://api.whatsapp.com/send?text=${title} - ${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "linkedin":
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        default:
          console.error("Unsupported platform:", platform);
      }
    },
    
  },
  
  mounted() {
    document.addEventListener('keydown', this.onEscKey);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onEscKey);
    
    // Clean up game resources
    this.cleanupGame();
  },
  
  async created() {
    // Fetch game data
    await this.fetchGame(this.$route.params.slug);
    
    // Check access
    this.checkAccess();
  },
  
  // Watch for changes - simple watchers like xceed-server
  watch: {
    game() {
      if (this.game?.url) {
        this.checkAccess();
      }
    },
    
    isLoggedIn() {
      this.checkAccess();
    },
    
    userInfo: {
      handler() {
        this.checkAccess();
      },
      deep: true
    }
  }
};
</script>

<style>
.text-gray {
  color: #A4A4A4 !important;
}

.fs-7 {
  font-size: .875rem;
  line-height: 1.25rem;
}

.game-container {
  position: relative;
  width: 100%;
  height: 70vh;
}

.game-frame {
  width: 100%;
  height: 100%;
  border: none;
  aspect-ratio: 16/9;
}

.access-denied-container {
  height: 70vh;
  background-color: #262653;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.access-denied-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.subscription-message {
  background-color: rgba(15, 64, 134, 0.733);
  border-radius: 12px;
  margin: 0 32px;
  cursor: pointer;
}

.loading-state {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(38, 38, 83, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>